import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Camera, Trash2, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";

const MAX_MB = 5;
const OUT = 512;

function loadImage(file) {
  return new Promise((res, rej) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => res({ img, url });
    img.onerror = () => { URL.revokeObjectURL(url); rej(new Error("Imagem inválida")); };
    img.src = url;
  });
}

function cropToBlob(img, zoom) {
  const side = Math.min(img.naturalWidth, img.naturalHeight) / zoom;
  const sx = (img.naturalWidth - side) / 2, sy = (img.naturalHeight - side) / 2;
  const c = document.createElement("canvas"); c.width = OUT; c.height = OUT;
  c.getContext("2d").drawImage(img, sx, sy, side, side, 0, 0, OUT, OUT);
  return new Promise((res, rej) => c.toBlob((b) => (b && b.size ? res(b) : rej(new Error("Não foi possível processar a imagem neste dispositivo"))), "image/jpeg", 0.88));
}

export function ProfilePhotoEditor() {
  const { user, setUser } = useAuth();
  const inputRef = useRef(null);
  const [pick, setPick] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [busy, setBusy] = useState(false);
  const [confirmRm, setConfirmRm] = useState(false);

  useEffect(() => () => { if (pick?.url) URL.revokeObjectURL(pick.url); }, [pick]);

  const onFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Apenas imagens são permitidas");
    if (file.size > MAX_MB * 1024 * 1024) return toast.error(`Imagem demasiado grande (máx. ${MAX_MB}MB)`);
    try { const { img, url } = await loadImage(file); setZoom(1); setPick({ img, url }); }
    catch (err) { toast.error(err.message); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const blob = await cropToBlob(pick.img, zoom);
      const fd = new FormData(); fd.append("file", blob, "profile.jpg"); fd.append("purpose", "profile_photo");
      const { data: up } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await api.patch("/profile", { photo_url: up.url });
      setUser(data); setPick(null); toast.success("Foto de perfil actualizada");
    } catch (err) { toast.error(err.response ? (formatApiError(err.response.data?.detail) || "Erro ao guardar a foto") : (err.message || "Erro ao guardar a foto")); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    setConfirmRm(false);
    setBusy(true);
    try { const { data } = await api.delete("/profile/photo"); setUser(data); toast.success("Foto removida"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Erro ao remover a foto"); }
    finally { setBusy(false); }
  };

  return (
    <div className="relative shrink-0">
      <Avatar src={user.photo_url} name={user.name} size={64} testId="profile-avatar" />
      <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} data-testid="profile-photo-change" aria-label={user.photo_url ? "Alterar foto" : "Adicionar foto"}
              className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-[#c8102e] text-white flex items-center justify-center border-2 border-white shadow hover:bg-[#a50c24] transition-colors disabled:opacity-50">
        <Camera size={13} />
      </button>
      {user.photo_url && (
        <button type="button" onClick={() => setConfirmRm(true)} disabled={busy} data-testid="profile-photo-remove" aria-label="Remover foto"
                className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-white text-zinc-700 border border-zinc-300 flex items-center justify-center shadow hover:text-[#c8102e] transition-colors disabled:opacity-50">
          <Trash2 size={11} />
        </button>
      )}
      <input ref={inputRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="profile-photo-input" />

      {confirmRm && createPortal(
        <div className="fixed inset-0 z-[80] bg-black/60 flex items-end sm:items-center justify-center p-4 pb-24 sm:pb-4" data-testid="profile-photo-remove-confirm">
          <div className="bg-white rounded-2xl w-full max-w-sm p-4">
            <div className="font-display font-bold mb-1">Remover a foto de perfil?</div>
            <p className="text-xs text-zinc-500 mb-4">Voltarás a aparecer com as tuas iniciais.</p>
            <div className="flex gap-2">
              <button type="button" onClick={() => setConfirmRm(false)} disabled={busy} data-testid="profile-photo-remove-cancel" className="flex-1 py-2.5 rounded-xl border border-zinc-300 text-sm font-semibold">Cancelar</button>
              <button type="button" onClick={remove} disabled={busy} data-testid="profile-photo-remove-yes" className="flex-1 py-2.5 rounded-xl bg-[#c8102e] hover:bg-[#a50c24] text-white text-sm font-semibold disabled:opacity-50">{busy ? "A remover..." : "Remover"}</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {pick && createPortal(
        <div className="fixed inset-0 z-[80] bg-black/60 flex items-end sm:items-center justify-center p-4 pb-24 sm:pb-4" data-testid="profile-photo-preview">
          <div className="bg-white rounded-2xl w-full max-w-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display font-bold">Pré-visualizar foto</div>
              <button type="button" onClick={() => setPick(null)} data-testid="profile-photo-cancel" className="p-1 rounded-full hover:bg-zinc-100"><X size={18} /></button>
            </div>
            <div className="mx-auto w-48 h-48 rounded-full overflow-hidden bg-zinc-100 relative">
              <img src={pick.url} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ transform: `scale(${zoom})` }} />
            </div>
            <label className="block mt-4 text-xs text-zinc-600">Ajustar enquadramento
              <input type="range" min="1" max="3" step="0.05" value={zoom} onChange={(e) => setZoom(Number(e.target.value))} data-testid="profile-photo-zoom" className="w-full accent-[#c8102e]" />
            </label>
            <p className="text-[11px] text-zinc-500 mt-1">A foto é recortada em formato quadrado e redimensionada para {OUT}×{OUT}.</p>
            <div className="flex gap-2 mt-4">
              <button type="button" onClick={() => setPick(null)} className="flex-1 py-2.5 rounded-xl border border-zinc-300 text-sm font-semibold">Cancelar</button>
              <button type="button" onClick={save} disabled={busy} data-testid="profile-photo-save" className="flex-1 py-2.5 rounded-xl bg-[#c8102e] hover:bg-[#a50c24] text-white text-sm font-semibold disabled:opacity-50">{busy ? "A guardar..." : "Guardar foto"}</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
